import { useCallback, useEffect, useState, useMemo } from "react";
import { useFormContext } from "../../../../common/contexts/FormContext";
import {
  mapZoomLevel,
  SESSION_FORMVALUES,
  SESSION_SERVICE_API_ERROR,
  sharedStateKeys,
} from "../../../../constants/constant";
import { hereMapApiKey } from "../../../../service/apiConfig";
import InteractiveMap from "./InteractiveMap";
import { getPropertyLawnAreaCoordinates } from "./coordinate";
import { convertGeoJSONToPropertyAreas } from "./util";
import { useFormConfigContext } from "../../../../common/contexts/FormConfigContext";
import isInboundFromNbly from "../../../../utility/checkInBoundFromNbly";
import { sessionStorageUtils } from "../../../../utility/utility";
import { useSharedStateWithActions } from "../../../../common/hooks/useSharedState";

interface PropertyConfirmationProps {
  handlePropertySelectedArea: (selectedAreas: {
    totalSelectedArea: string;
  }) => void;
  onToggleLeftSidebar?: () => void;
  deepLawnPolygon?: boolean;
}

const PropertyConfirmation = ({
  handlePropertySelectedArea,
  onToggleLeftSidebar,
  deepLawnPolygon = false,
}: PropertyConfirmationProps) => {
  const { formValues, setFormValues } = useFormContext();
  const { formConfig } = useFormConfigContext();
  const [propertyAreas, setPropertyAreas] = useState<any[]>([]);
  const [center, setCenter] = useState<[number, number]>([40.7128, -74.006]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { setValue } = useSharedStateWithActions();

  useEffect(() => {
    const loadPropertyArea = async () => {
      try {
        const coordinatesData = await getPropertyLawnAreaCoordinates(
          formValues,
          formConfig,
        );

        setValue(sharedStateKeys.IS_INTERACTIVE_MAP_READY, true);
        const areas = convertGeoJSONToPropertyAreas(
          coordinatesData,
          deepLawnPolygon,
        );
        const areasWithSelection = areas.map((area) => ({
          ...area,
          selected: true,
        }));

        const allAreasHaveZeroSize = areasWithSelection.every(
          (area) => !area.size || area.size === 0,
        );

        if (allAreasHaveZeroSize && areasWithSelection.length > 0) {
          // Call handler with 0 before redirecting
          handlePropertySelectedArea({ totalSelectedArea: "0" });
          const redirectionUrl =
            formConfig.redirectionPageUrl ?? "/confirmation";
          sessionStorageUtils.setItem(SESSION_FORMVALUES, formValues);
          sessionStorageUtils.setItem(SESSION_SERVICE_API_ERROR, true);
          if (isInboundFromNbly()) {
            globalThis.location.replace(
              `${redirectionUrl}?leadconcept=${formConfig.conceptCode}`,
            );
            return;
          }
          globalThis.location.replace(redirectionUrl);
          return;
        }

        setPropertyAreas(areasWithSelection);

        if (areasWithSelection.length > 0) {
          const allCoordinates = areasWithSelection.flatMap(
            (area) => area.coordinates,
          );
          const centerLat =
            allCoordinates.reduce((sum, coord) => sum + coord[0], 0) /
            allCoordinates.length;
          const centerLng =
            allCoordinates.reduce((sum, coord) => sum + coord[1], 0) /
            allCoordinates.length;
          setCenter([centerLat, centerLng]);
        }
      } catch (error) {
        console.error("Error loading property area:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadPropertyArea();
  }, []);

  const handleAreaSelect = useCallback(
    (totalSelectedArea: number) => {
      setFormValues((prevValues) => ({
        ...prevValues,
        "estimate-lawn-size": totalSelectedArea,
      }));
      const formattedSize = totalSelectedArea.toLocaleString();
      handlePropertySelectedArea({ totalSelectedArea: formattedSize });
    },
    [handlePropertySelectedArea, setFormValues],
  );

  const memoizedHereConfig = useMemo(
    () => ({
      apiKey: hereMapApiKey[process.env.NODE_ENV || "development"],
    }),
    [],
  );

  return (
    <div>
      {isLoading && (
        <div className="map-loading">
          <div className="loading-spinner"></div>
        </div>
      )}
      <InteractiveMap
        center={center}
        zoom={mapZoomLevel}
        propertyAreas={propertyAreas}
        onAreaSizeCalculate={handleAreaSelect}
        hereConfig={memoizedHereConfig}
        onToggleLeftSidebar={onToggleLeftSidebar}
      />
    </div>
  );
};

export default PropertyConfirmation;
